import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  // useRef,
  useState,
} from 'react';
import axios from 'axios';
import moment from 'moment';
import { AiOutlineCaretDown } from 'react-icons/ai';
import { FiRefreshCw } from 'react-icons/fi';
import { Input } from 'reactstrap';
import { authContext } from 'contexts/AuthContext';
// import { FSBTable } from 'components/common/FSBTable';
import Heading from 'components/Heading';
import LineChart from '../Dashboard/components/DashboardCardChart/components/LineChart';
// import DateRangeFilter from './DateRangeFilter';

const API_BASE_URL = process.env.REACT_APP_REST_API_ENDPOINT;

const parseReportDate = (reportDate) => {
  if (Array.isArray(reportDate) && reportDate.length >= 3) {
    const [year, month, day] = reportDate;
    return moment([year, month - 1, day]);
  }

  if (typeof reportDate === 'string' || reportDate instanceof Date) {
    return moment(reportDate);
  }

  return moment.invalid();
};

const formatReportDate = (reportDate) => {
  const parsedDate = parseReportDate(reportDate);

  return parsedDate.isValid() ? parsedDate.format('D MMM') : '-';
};

const formatDateLabel = (reportDate) => {
  const parsedDate = parseReportDate(reportDate);

  return parsedDate.isValid() ? parsedDate.format('D MMM, YYYY') : '-';
};

const toSafeNumber = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

/*
const dailyPriceTableColumns = [
  { accessorKey: 'serialNo', header: 'S.No', enableSorting: false },
  { accessorKey: 'date', header: 'Date', enableSorting: false },
  { accessorKey: 'activeUsers', header: 'Active user', enableSorting: false },
  { accessorKey: 'inactiveUsers', header: 'Inactive user', enableSorting: false },
  { accessorKey: 'clicked', header: 'Clicked', enableSorting: false },
];
*/

const DailyPriceSharing = () => {
  const { auth } = useContext(authContext);
  const [selectedPeriod, setSelectedPeriod] = useState('last_7_days');
  const [dailyPriceData, setDailyPriceData] = useState([]);
  // const [dailyActivityData, setDailyActivityData] = useState([]);
  // const [activityDateRange, setActivityDateRange] = useState({
  //   startDate: null,
  //   endDate: null,
  // });
  // const [activityTotalElements, setActivityTotalElements] = useState(0);
  // const [activityTotalPages, setActivityTotalPages] = useState(0);
  // const [activityLoading, setActivityLoading] = useState(false);
  // const [activityErrorMessage, setActivityErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // const [tablePageNo, setTablePageNo] = useState(1);
  // const [tablePageSize, setTablePageSize] = useState(10);
  // const activityRequestId = useRef(0);

  const dateRange = useMemo(() => {
    const endDate = moment();
    const startDate = endDate.clone().subtract(
      selectedPeriod === 'last_30_days' ? 29 : 6,
      'days',
    );

    return {
      fromDate: startDate.format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
      label: `${startDate.format('D MMM')} - ${endDate.format('D MMM')}`,
    };
  }, [selectedPeriod]);

  const fetchDailyPriceSharing = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      if (!auth?.data?.token) {
        throw new Error('Authentication token not found.');
      }

      const response = await axios.get(
        `${API_BASE_URL}analytics/daily-price-sharing`,
        {
          params: {
            fromDate: dateRange.fromDate,
            toDate: dateRange.toDate,
          },
          headers: {
            Authorization: `Bearer ${auth?.data?.token}`,
          },
        },
      );

      const rawData = response?.data?.data ?? response?.data ?? [];
      const normalizedData = Array.isArray(rawData)
        ? rawData
          .map((item) => ({
            reportDate: item?.reportDate,
            reportDateLabel: formatDateLabel(item?.reportDate),
            chartLabel: formatReportDate(item?.reportDate),
            dailyPriceActiveUsers: toSafeNumber(item?.dailyPriceActiveUsers),
            dailyPriceInactiveUsers: toSafeNumber(item?.dailyPriceInactiveUsers),
            dailyPriceClickedUsers: item?.dailyPriceClickedUsers
              ?? item?.clickedUsers
              ?? item?.clickedCount
              ?? item?.clicked,
            sortValue: parseReportDate(item?.reportDate).valueOf(),
          }))
          .sort((a, b) => a.sortValue - b.sortValue)
        : [];

      setDailyPriceData(normalizedData);
      setLastUpdatedAt(new Date());
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load active/inactive users data.',
      );
    } finally {
      setLoading(false);
    }
  }, [auth?.data?.token, dateRange.fromDate, dateRange.toDate]);

  useEffect(() => {
    fetchDailyPriceSharing();
  }, [fetchDailyPriceSharing]);

  /*
  const fetchDailyActivity = useCallback(async () => {
    const requestId = activityRequestId.current + 1;
    activityRequestId.current = requestId;
    setActivityLoading(true);
    setActivityErrorMessage('');

    try {
      if (!auth?.data?.token) {
        throw new Error('Authentication token not found.');
      }

      const params = {
        page: tablePageNo - 1,
        size: tablePageSize,
      };

      if (activityDateRange.startDate && activityDateRange.endDate) {
        params.startDate = moment(activityDateRange.startDate).format('YYYY-MM-DD');
        params.endDate = moment(activityDateRange.endDate).format('YYYY-MM-DD');
      }

      const response = await axios.get(
        `${API_BASE_URL}analytics/daily-price/activity`,
        {
          params,
          headers: {
            Authorization: `Bearer ${auth?.data?.token}`,
          },
        },
      );

      if (activityRequestId.current !== requestId) {
        return;
      }

      const responseData = response?.data ?? {};
      setDailyActivityData(Array.isArray(responseData.content) ? responseData.content : []);
      setActivityTotalElements(toSafeNumber(responseData.totalElements));
      setActivityTotalPages(toSafeNumber(responseData.totalPages));
    } catch (error) {
      if (activityRequestId.current !== requestId) {
        return;
      }

      setDailyActivityData([]);
      setActivityTotalElements(0);
      setActivityTotalPages(0);
      setActivityErrorMessage(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load daily activity details.',
      );
    } finally {
      if (activityRequestId.current === requestId) {
        setActivityLoading(false);
      }
    }
  }, [
    activityDateRange.endDate,
    activityDateRange.startDate,
    auth?.data?.token,
    tablePageNo,
    tablePageSize,
  ]);

  useEffect(() => {
    fetchDailyActivity();
  }, [fetchDailyActivity]);
  */

  const chartLabels = useMemo(
    () => dailyPriceData.map((item) => item.chartLabel),
    [dailyPriceData],
  );

  const activeUsersData = useMemo(
    () => dailyPriceData.map((item) => item.dailyPriceActiveUsers),
    [dailyPriceData],
  );

  const inactiveUsersData = useMemo(
    () => dailyPriceData.map((item) => item.dailyPriceInactiveUsers),
    [dailyPriceData],
  );

  /*
  const dailyPriceTableData = useMemo(() => {
    return dailyActivityData.map((item, index) => ({
      serialNo: ((tablePageNo - 1) * tablePageSize) + index + 1,
      date: formatDateLabel(item?.date),
      activeUsers: toSafeNumber(item?.activeUser),
      inactiveUsers: toSafeNumber(item?.inactiveUser),
      clicked: toSafeNumber(item?.clicked),
    }));
  }, [dailyActivityData, tablePageNo, tablePageSize]);

  const handleTablePageChange = ({ pageNo, pageSize }) => {
    setTablePageSize(pageSize);
    setTablePageNo(pageSize === tablePageSize ? pageNo : 1);
  };

  const handleActivityDateFilter = ({ startDate, endDate }) => {
    setActivityDateRange({ startDate, endDate });
    setTablePageNo(1);
  };
  */

  const lastUpdatedText = lastUpdatedAt
    ? moment(lastUpdatedAt).format('DD MMM YYYY, hh:mm A')
    : '';

  const dateRangeFilter = (
    <div className="chart-filter daily-price-sharing__chart-filter d-flex align-items-center position-relative">
      <div className="position-relative custom-arrow">
        <AiOutlineCaretDown aria-hidden="true" />
        <Input
          aria-label="Active/inactive users date range"
          name="active-inactive-users-period"
          type="select"
          className="border-0"
          value={selectedPeriod}
          onChange={(event) => setSelectedPeriod(event.target.value)}
        >
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
        </Input>
      </div>
      <span className="f-12 text-active ms-2 ps-1">{dateRange.label}</span>
    </div>
  );

  let bodyContent = null;

  if (loading) {
    bodyContent = (
      <div className="daily-price-sharing__chart-panel">
        <div className="daily-price-sharing__empty-state">
          Loading active/inactive users...
        </div>
        {dateRangeFilter}
      </div>
    );
  } else if (dailyPriceData.length === 0) {
    bodyContent = (
      <div className="daily-price-sharing__chart-panel">
        <div className="daily-price-sharing__empty-state">
          No active/inactive users data available.
        </div>
        {dateRangeFilter}
      </div>
    );
  } else {
    bodyContent = (
      <div className="daily-price-sharing__chart-panel">
        <LineChart
          combinedKeys={chartLabels}
          firstLineData={activeUsersData}
          secondLineData={inactiveUsersData}
          firstHeading="Active Users"
          secondHeading="Inactive Users"
          hideCheckboxes
        />
        {dateRangeFilter}
      </div>
    );
  }

  return (
    <div className="daily-price-sharing-container">
      <div className="daily-price-sharing__header">
        <div>
          <Heading title="Daily Price Analytics" />
        </div>

        <div className="daily-price-sharing__actions">
          {lastUpdatedText ? (
            <span className="daily-price-sharing__updated">
              Updated {lastUpdatedText}
            </span>
          ) : null}

          <button
            type="button"
            className="daily-price-sharing__refresh"
            onClick={fetchDailyPriceSharing}
            disabled={loading}
            title="Refresh active/inactive users"
          >
            <FiRefreshCw className={loading ? 'is-spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="daily-price-sharing__error" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {bodyContent}

      {/* <section
        className="daily-price-sharing__table-section"
        aria-label="Daily price activity details"
      >
        <div className="daily-price-sharing__table-heading">
          <h4 className="daily-price-sharing__table-title">Daily Activity Details</h4>
          <div className="daily-price-sharing__activity-filter">
            <DateRangeFilter
              dateRange={activityDateRange}
              onFilter={handleActivityDateFilter}
            />
          </div>
        </div>
        {activityErrorMessage ? (
          <div className="daily-price-sharing__error" role="alert">
            {activityErrorMessage}
          </div>
        ) : null}
        <FSBTable
          data={dailyPriceTableData}
          columns={dailyPriceTableColumns}
          totalCount={activityTotalElements}
          totalPages={activityTotalPages}
          currentPageNo={tablePageNo}
          pageSize={tablePageSize}
          keyField="date"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          remote
          striped={false}
          hover={false}
          bordered={false}
          loading={activityLoading}
          wrapperClass="table-responsive word-break daily-price-sharing__table"
          tableWraperClass="daily-price-sharing__table-border"
          handlePageChange={handleTablePageChange}
        />
      </section> */}
    </div>
  );
};

export default DailyPriceSharing;
